"use strict";
/**
 * Input Validation Utilities
 *
 * Provides reusable validation functions for form inputs
 * to ensure data integrity and prevent invalid submissions.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatToDecimalPlaces = exports.hasAtLeastOnePositiveValue = exports.isWithinRange = exports.sanitizeWhitespace = exports.isWhitespaceOnly = exports.isNonNegativeNumber = exports.isPositiveNumber = exports.sanitizeDecimalInput = exports.isValidDecimal = exports.sanitizeNumericInput = exports.isNumericOnly = void 0;
/**
 * Check if a value contains only numeric characters (0-9)
 * @param value - The string to validate
 * @returns true if value contains only digits, false otherwise
 */
var isNumericOnly = function (value) {
    if (!value || value.trim() === "")
        return true; // Empty is considered valid
    return /^\d+$/.test(value);
};
exports.isNumericOnly = isNumericOnly;
/**
 * Remove all non-numeric characters from a string
 * @param value - The string to sanitize
 * @returns String containing only digits
 */
var sanitizeNumericInput = function (value) {
    return value.replace(/\D/g, "");
};
exports.sanitizeNumericInput = sanitizeNumericInput;
/**
 * Check if a value is a valid decimal number with specified max decimal places
 * @param value - The string to validate
 * @param maxDecimals - Maximum number of decimal places allowed (default: 2)
 * @returns true if value is a valid decimal number, false otherwise
 */
var isValidDecimal = function (value, maxDecimals) {
    if (maxDecimals === void 0) { maxDecimals = 2; }
    if (!value || value.trim() === "")
        return true; // Empty is considered valid
    // Pattern: optional digits before decimal, optional decimal point, max N digits after
    var pattern = new RegExp("^\\d+(\\.\\d{0,".concat(maxDecimals, "})?$"));
    return pattern.test(value);
};
exports.isValidDecimal = isValidDecimal;
/**
 * Sanitize decimal input to ensure it has max specified decimal places
 * @param value - The string to sanitize
 * @param maxDecimals - Maximum number of decimal places allowed (default: 2)
 * @returns Sanitized decimal string
 */
var sanitizeDecimalInput = function (value, maxDecimals) {
    if (maxDecimals === void 0) { maxDecimals = 2; }
    // Remove any non-numeric characters except decimal point
    var sanitized = value.replace(/[^\d.]/g, "");
    // Ensure only one decimal point
    var parts = sanitized.split(".");
    if (parts.length > 2) {
        sanitized = parts[0] + "." + parts.slice(1).join("");
    }
    // Limit decimal places
    if (parts.length === 2) {
        var integerPart = parts[0];
        var decimalPart = parts[1].slice(0, maxDecimals);
        sanitized = integerPart + "." + decimalPart;
    }
    return sanitized;
};
exports.sanitizeDecimalInput = sanitizeDecimalInput;
/**
 * Check if a value is a positive number (greater than 0)
 * @param value - The string to validate
 * @returns true if value is a positive number, false otherwise
 */
var isPositiveNumber = function (value) {
    if (!value || value.trim() === "")
        return false;
    var num = parseFloat(value);
    return !isNaN(num) && num > 0;
};
exports.isPositiveNumber = isPositiveNumber;
/**
 * Check if a value is a non-negative number (greater than or equal to 0)
 * @param value - The string to validate
 * @returns true if value is non-negative, false otherwise
 */
var isNonNegativeNumber = function (value) {
    if (!value || value.trim() === "")
        return false;
    var num = parseFloat(value);
    return !isNaN(num) && num >= 0;
};
exports.isNonNegativeNumber = isNonNegativeNumber;
/**
 * Check if a string contains only whitespace
 * @param value - The string to check
 * @returns true if value is only whitespace or empty, false otherwise
 */
var isWhitespaceOnly = function (value) {
    return !value || value.trim() === "";
};
exports.isWhitespaceOnly = isWhitespaceOnly;
/**
 * Trim whitespace and return empty string if only whitespace
 * @param value - The string to sanitize
 * @returns Trimmed string or empty string if only whitespace
 */
var sanitizeWhitespace = function (value) {
    var trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : "";
};
exports.sanitizeWhitespace = sanitizeWhitespace;
/**
 * Validate that a numeric string is within a range
 * @param value - The string to validate
 * @param min - Minimum allowed value (inclusive)
 * @param max - Maximum allowed value (inclusive)
 * @returns true if value is within range, false otherwise
 */
var isWithinRange = function (value, min, max) {
    if (!value || value.trim() === "")
        return false;
    var num = parseFloat(value);
    return !isNaN(num) && num >= min && num <= max;
};
exports.isWithinRange = isWithinRange;
/**
 * Check if at least one value in an array is greater than zero
 * @param values - Array of numeric strings to check
 * @returns true if at least one value is > 0, false otherwise
 */
var hasAtLeastOnePositiveValue = function (values) {
    return values.some(function (value) {
        var num = parseFloat(value);
        return !isNaN(num) && num > 0;
    });
};
exports.hasAtLeastOnePositiveValue = hasAtLeastOnePositiveValue;
/**
 * Format a number to have exactly N decimal places
 * @param value - The number or string to format
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
var formatToDecimalPlaces = function (value, decimalPlaces) {
    if (decimalPlaces === void 0) { decimalPlaces = 2; }
    var num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num))
        return "0.00";
    return num.toFixed(decimalPlaces);
};
exports.formatToDecimalPlaces = formatToDecimalPlaces;
